/**
 * Shared OAuth popup flow for connector init / disconnect.
 *
 * Both apps (ari, ari-cloud) use the same popup flow.
 * They differ only in how they make authenticated requests, so the caller
 * provides an `authFetch` function with the same signature as `window.fetch`.
 */

/**
 * Structured connector-connect error codes. `timeout` and `access_denied`
 * (popup closed) are produced client-side; the rest come from the edge
 * function callback redirect (see _shared/auth/connect-errors.ts).
 */
export type ConnectionErrorCode =
  | 'admin_consent_required'
  | 'access_denied'
  | 'rate_limited'
  | 'connection_failed'
  | 'timeout';

export interface ConnectionResult {
  connected?: string;
  /** The connector whose vault session the user just refreshed (CLOUD-1040). */
  reauthorized?: string;
  error?: ConnectionErrorCode | string;
  errorDescription?: string;
}

/** Popup → opener result contract. Also used by apps/{ari,ari-cloud}/public/oauth-error.html. */
export const CONNECT_RESULT_TYPE = 'oauth-connect-result';
export const CONNECT_CHANNEL = 'oauth-connect';
export const CONNECT_ATTEMPT_KEY = 'oauth-connect-attempt';

export interface ConnectResultMessage extends ConnectionResult {
  type: typeof CONNECT_RESULT_TYPE;
  attemptId: string | null;
}

const CLOSE_GRACE_MS = 2_000;

// One popup at a time. A stale name reuse used to give this for free; '_blank' does not.
let activePopup: Window | null = null;

/**
 * Initiate OAuth connection in a centered popup.
 *
 * @param authFetch - Authenticated fetch function (same signature as window.fetch)
 * @param baseUrl - Auth backend base URL (e.g. from getAuthBackendUrl())
 * @param connectorId - Connector ID (primary key, e.g. "microsoft_copilot")
 */
export type AuthFetchFn = (url: string, init?: RequestInit) => Promise<Response>;

export async function initiateConnection(
  authFetch: AuthFetchFn,
  baseUrl: string,
  connectorId: string
): Promise<ConnectionResult> {
  return runOAuthPopup(authFetch, `${baseUrl}/auth/connectors/${connectorId}/connect`);
}

/** Refresh the vault session before a removal, in the connect popup. Resolves with `reauthorized` set on success. */
export async function reauthorizeConnector(
  authFetch: AuthFetchFn,
  baseUrl: string,
  connectorId: string
): Promise<ConnectionResult> {
  return runOAuthPopup(authFetch, `${baseUrl}/auth/connectors/${connectorId}/reauthorize`);
}

/** Open the popup first, so it counts as the user's click, then ask `url` where to send it. */
async function runOAuthPopup(authFetch: AuthFetchFn, url: string): Promise<ConnectionResult> {
  const width = 520;
  const height = 700;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;

  if (activePopup && !activePopup.closed) activePopup.close();

  // Use a real page instead of about:blank to preserve browser session context
  // across the OAuth redirect chain (about:blank breaks cookie tracking in
  // modern browsers, causing AADSTS165000 errors with Microsoft login)
  const popup = window.open(
    '/oauth-loading.html',
    '_blank',
    `width=${width},height=${height},left=${left},top=${top},popup=yes`
  );

  if (!popup) throw new Error('Popup blocked by browser');

  activePopup = popup;

  const returnTo = window.location.origin + window.location.pathname;
  const attemptId = crypto.randomUUID();

  try {
    // Session storage belongs to the popup tab, so it survives a cut opener link.
    popup.sessionStorage.setItem(CONNECT_ATTEMPT_KEY, attemptId);
  } catch {
    // Storage can be blocked or full. Continue without a stored attemptId.
  }

  try {
    const response = await authFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ returnTo }),
    });

    if (!response.ok) {
      popup.close();
      throw new Error(`Failed to initiate connection: ${response.status}`);
    }

    const data = await response.json();

    popup.location.href = data.redirect_url || data.authorize_url;
  } catch (error) {
    popup.close();
    throw error;
  }

  return new Promise<ConnectionResult>((resolve) => {
    const channel = new BroadcastChannel(CONNECT_CHANNEL);
    let closedSince: number | undefined;

    const finish = (result: ConnectionResult) => {
      clearInterval(closedCheck);
      clearTimeout(timeout);
      window.removeEventListener('message', onWindowMessage);
      channel.close();
      resolve(result);
    };

    const onResult = (data: Partial<ConnectResultMessage> | undefined, acceptNullAttempt = false) => {
      if (data?.type !== CONNECT_RESULT_TYPE) return;
      const isMatch = data.attemptId === attemptId || (acceptNullAttempt && data.attemptId === null);
      if (!isMatch) return;
      finish({
        connected: data.connected,
        reauthorized: data.reauthorized,
        error: data.error,
        errorDescription: data.errorDescription,
      });
    };

    // The popup's sessionStorage write can fail, so it may report a null attemptId.
    // Trust that only from the popup itself, never from the BroadcastChannel.
    const onWindowMessage = (event: MessageEvent) => {
      if (event.origin === window.location.origin && event.source === popup) onResult(event.data, true);
    };

    window.addEventListener('message', onWindowMessage);
    channel.onmessage = (event) => onResult(event.data);

    // A COOP page cuts the opener link, and then `closed` is true for a live popup.
    // Report a cancel only after the user is back in this window for CLOSE_GRACE_MS.
    const closedCheck = setInterval(() => {
      if (!popup.closed || !document.hasFocus()) {
        closedSince = undefined;
        return;
      }
      closedSince ??= Date.now();
      if (Date.now() - closedSince >= CLOSE_GRACE_MS) finish({ error: 'access_denied' });
    }, 300);

    const timeout = setTimeout(() => {
      if (!popup.closed) popup.close();
      finish({ error: 'timeout' });
    }, 300_000);
  });
}

/**
 * Disconnect a connector.
 *
 * @param authFetch - Authenticated fetch function
 * @param baseUrl - Auth backend base URL
 * @param connectorId - Connector ID to disconnect
 */
export async function disconnectProvider(
  authFetch: AuthFetchFn,
  baseUrl: string,
  connectorId: string
): Promise<boolean> {
  const response = await authFetch(`${baseUrl}/auth/connectors/${connectorId}`, {
    method: 'DELETE',
  });
  return response.ok;
}
